import { useState } from 'react';
import { CheckCircle2, Eye, UploadCloud, X } from 'lucide-react';
import AppShell from '../app/AppShell';
import {
  FeedbackMessage,
  PaginationFooter,
  SaveConfirmationModal,
  ToastStack,
} from '../components/common';
import {
  SalesOrderEditModal,
  SalesOrderFilters,
  SalesOrderForm,
  SalesOrderTable,
  SalesOrderToolbar,
} from '../components/sales-order';
import { createSalesOrderFormInitialValues } from '../data/salesOrderConstants';
import useCancelConfirmation from '../hooks/useCancelConfirmation';
import useSalesOrders from '../hooks/useSalesOrders';
import useSaveConfirmation from '../hooks/useSaveConfirmation';
import { hasChangedFormValues } from '../utils/formDirty';
import { convertImageFileToWebpDataUrl, MAX_IMAGE_UPLOAD_BYTES } from '../utils/imageConverter';

function SalesOrderPage({ navigationItems, activePath, onNavigate }) {
  const saveConfirmation = useSaveConfirmation();
  const cancelConfirmation = useCancelConfirmation();
  const [deletingSalesOrder, setDeletingSalesOrder] = useState(null);
  const [reopeningSalesOrder, setReopeningSalesOrder] = useState(null);
  const [billingSalesOrder, setBillingSalesOrder] = useState(null);
  const {
    activeView,
    filterValues,
    salesOrders,
    salesOrderPagination,
    salesOrderSearchSuggestions,
    salesOrderStatusOptions,
    customerOptions,
    seaweedTypeOptions,
    customerBranchOptions,
    editCustomerBranchOptions,
    containerOptions,
    selectedContainer,
    selectedEditContainer,
    formValues,
    formErrors,
    editingSalesOrder,
    editValues,
    editErrors,
    isLoading,
    isSaving,
    isEditSaving,
    isBillingSaving,
    loadError,
    submitError,
    editError,
    billingError,
    successMessage,
    canCreate,
    setActiveView,
    handleFilterChange,
    handleFilterSubmit,
    handleStatusFilterChange,
    handleFilterReset,
    handleFormChange,
    handleSubmit,
    handleCancel,
    openEditModal,
    closeEditModal,
    handleEditChange,
    handleEditSubmit,
    handleDelete,
    handleReopen,
    handleBillingSubmit,
  } = useSalesOrders({
    includeCustomerBranch: true,
    includeSeaweedType: true,
    includeCommercialFields: true,
    includeContainerDetails: false,
    includeWarehouseLocation: false,
  });
  const toasts = [
    loadError ? { id: 'so-load-error', tone: 'error', message: loadError } : null,
    submitError ? { id: 'so-submit-error', tone: 'error', message: submitError } : null,
    successMessage ? { id: 'so-success', tone: 'success', message: successMessage } : null,
    editError ? { id: 'so-edit-error', tone: 'error', message: editError } : null,
    billingError ? { id: 'so-billing-error', tone: 'error', message: billingError } : null,
  ].filter(Boolean);

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      onNavigate={onNavigate}
    >
      <ToastStack toasts={toasts} />
      <section className="mt-5 px-4 sm:mt-6 sm:px-6 lg:px-[26px]">
        <div className="grid gap-5">
          <SalesOrderToolbar activeView={activeView} onChangeView={setActiveView} />

          {activeView === 'list' ? (
            <div className="grid min-w-0 gap-3">
              <div className="min-w-0">
                <h2 className="m-0 text-[1.25rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.4rem]">
                  Sales Order List
                </h2>
                <p className="m-0 mt-2 max-w-full text-[0.96rem] leading-6 text-[var(--theme-text-muted)]">
                  Manage customer orders, container references, and warehouse delivery planning.
                </p>
              </div>

              <SalesOrderFilters
                values={filterValues}
                suggestions={salesOrderSearchSuggestions}
                statusOptions={salesOrderStatusOptions}
                onChange={handleFilterChange}
                onStatusChange={handleStatusFilterChange}
                onSubmit={handleFilterSubmit}
                onReset={handleFilterReset}
              />

              {isLoading ? (
                <FeedbackMessage tone="info" message="Loading sales orders..." />
              ) : (
                <div className="grid gap-3">
                  <SalesOrderTable
                    salesOrders={salesOrders}
                    onEditSalesOrder={openEditModal}
                    onDeleteSalesOrder={setDeletingSalesOrder}
                    onReopenSalesOrder={setReopeningSalesOrder}
                    onUpdateBilling={setBillingSalesOrder}
                    includeCustomerBranch
                    includeSeaweedType
                    includeCommercialFields
                    includeContainerDetails={false}
                    includeWarehouseLocation={false}
                  />
                  {salesOrderPagination.totalItems ? (
                    <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
                      <PaginationFooter {...salesOrderPagination} />
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ) : (
            <>
              {!isLoading && !customerOptions.length ? (
                <FeedbackMessage
                  tone="warning"
                  message="Add at least one customer first so you can create a sales order."
                  action={{
                    label: 'Add Customer Profile',
                    onClick: () => onNavigate('/admin/customer?view=create'),
                  }}
                />
              ) : null}
              {!isLoading && !seaweedTypeOptions.length ? (
                <FeedbackMessage
                  tone="warning"
                  message="Add at least one seaweed type first so you can create a sales order."
                  action={{
                    label: 'Add Seaweed Type',
                    onClick: () => onNavigate('/admin/lansa?tab=seaweed-type'),
                  }}
                />
              ) : null}
              <SalesOrderForm
                values={formValues}
                errors={formErrors}
                customerOptions={customerOptions}
                seaweedTypeOptions={seaweedTypeOptions}
                customerBranchOptions={customerBranchOptions}
                containerOptions={containerOptions}
                selectedContainer={selectedContainer}
                isSaving={isSaving}
                canCreate={canCreate}
                includeCustomerBranch
                includeSeaweedType
                includeCommercialFields
                includeContainerDetails={false}
                includeWarehouseLocation={false}
                onChange={handleFormChange}
                onSubmit={(event) =>
                  saveConfirmation.requestConfirmation(event, handleSubmit, {
                    title: 'Save sales order?',
                    message: 'Please confirm the sales order details before saving.',
                    confirmLabel: 'Save S.O.',
                  })
                }
                onCancel={(event) =>
                  cancelConfirmation.requestCancel(event, {
                    shouldConfirm: () =>
                      hasChangedFormValues(
                        formValues,
                        getSalesOrderCreateBaseline(customerOptions, seaweedTypeOptions)
                      ),
                    onCancel: handleCancel,
                    title: 'Cancel sales order?',
                    message: 'You have unsaved sales order inputs. Discard them?',
                  })
                }
              />
            </>
          )}
        </div>
      </section>

      {editingSalesOrder ? (
        <SalesOrderEditModal
          values={editValues}
          errors={editErrors}
          customerOptions={customerOptions}
          seaweedTypeOptions={seaweedTypeOptions}
          customerBranchOptions={editCustomerBranchOptions}
          containerOptions={containerOptions}
          selectedContainer={selectedEditContainer}
          isSaving={isEditSaving}
          includeCustomerBranch
          includeSeaweedType
          includeCommercialFields
          includeContainerDetails={false}
          includeWarehouseLocation={false}
          onChange={handleEditChange}
          onClose={(event) =>
            cancelConfirmation.requestCancel(event, {
              shouldConfirm: () =>
                editingSalesOrder
                  ? hasChangedFormValues(
                      editValues,
                      getSalesOrderEditBaseline(editingSalesOrder)
                    )
                  : false,
              onCancel: closeEditModal,
              title: 'Cancel editing?',
              message: 'You have unsaved sales order changes. Discard them?',
            })
          }
          onSubmit={(event) =>
            saveConfirmation.requestConfirmation(event, handleEditSubmit, {
              title: 'Save sales order changes?',
              message: 'Please confirm the updated sales order details before saving.',
              confirmLabel: 'Save Changes',
            })
          }
        />
      ) : null}

      {deletingSalesOrder ? (
        <SaveConfirmationModal
          isOpen
          title="Cancel sales order?"
          message={`This will mark ${formatSalesOrderCode(deletingSalesOrder)} as cancelled and disable its row actions. Continue?`}
          confirmLabel="Cancel Sales Order"
          confirmingLabel="Cancelling..."
          cancelLabel="Keep Sales Order"
          isConfirming={isSaving}
          onCancel={() => setDeletingSalesOrder(null)}
          onConfirm={async () => {
            const didDelete = await handleDelete(deletingSalesOrder);

            if (didDelete) {
              setDeletingSalesOrder(null);
            }
          }}
        />
      ) : null}

      {reopeningSalesOrder ? (
        <SaveConfirmationModal
          isOpen
          title="Reopen sales order?"
          message={`This will reopen the latest released FCL for ${formatSalesOrderCode(reopeningSalesOrder)} as a draft and change the sales order back to Partially Released. Continue?`}
          confirmLabel="Reopen"
          confirmingLabel="Reopening..."
          cancelLabel="Keep Completed"
          isConfirming={isSaving}
          onCancel={() => setReopeningSalesOrder(null)}
          onConfirm={async () => {
            const didReopen = await handleReopen(reopeningSalesOrder);

            if (didReopen) {
              setReopeningSalesOrder(null);
            }
          }}
        />
      ) : null}

      {billingSalesOrder ? (
        <SalesOrderBillingModal
          salesOrder={billingSalesOrder}
          isSaving={isBillingSaving}
          onClose={() => setBillingSalesOrder(null)}
          onSubmit={async (payload) => {
            const didSave = await handleBillingSubmit(billingSalesOrder, payload);

            if (didSave) {
              setBillingSalesOrder(null);
            }
          }}
        />
      ) : null}

      <SaveConfirmationModal {...saveConfirmation.modalProps} />
      <SaveConfirmationModal {...cancelConfirmation.modalProps} />
    </AppShell>
  );
}

function getSalesOrderCreateBaseline(customerOptions, seaweedTypeOptions) {
  if (customerOptions.length !== 1) {
    return createSalesOrderFormInitialValues();
  }

  return {
    ...createSalesOrderFormInitialValues(),
    customerId: customerOptions[0].value,
    branch: customerOptions[0].branches?.[0] || '',
    seaweedType: seaweedTypeOptions.length === 1 ? seaweedTypeOptions[0].value : '',
  };
}

function SalesOrderBillingModal({ salesOrder, isSaving, onClose, onSubmit }) {
  const [billingStatus, setBillingStatus] = useState(
    salesOrder.billingStatus === 'Paid' ? 'Paid' : 'Unpaid'
  );
  const [billingRemarks, setBillingRemarks] = useState(salesOrder.billingRemarks || '');
  const [proofPreview, setProofPreview] = useState(
    salesOrder.isBillingProofExpired ? '' : salesOrder.billingProofPreview || ''
  );
  const [removeBillingProofImage, setRemoveBillingProofImage] = useState(false);
  const [isConvertingProof, setIsConvertingProof] = useState(false);
  const [isProofViewerOpen, setIsProofViewerOpen] = useState(false);
  const [fileError, setFileError] = useState('');
  const hasStoredProof =
    Boolean(salesOrder.billingProofPreview) && !salesOrder.isBillingProofExpired;
  const hasPreview = Boolean(proofPreview);

  async function handleProofChange(event) {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      setFileError('Proof image must be 50MB or smaller.');
      return;
    }

    setIsConvertingProof(true);
    setFileError('');

    try {
      const convertedImage = await convertImageFileToWebpDataUrl(file);
      setProofPreview(convertedImage.dataUrl);
      setRemoveBillingProofImage(false);
    } catch (error) {
      setFileError(error.message || 'Unable to convert proof image to WEBP.');
    } finally {
      setIsConvertingProof(false);
    }
  }

  function clearProofImage() {
    setProofPreview('');
    setIsProofViewerOpen(false);
    setRemoveBillingProofImage(hasStoredProof);
    setFileError('');
  }

  function handleSubmit(event) {
    event.preventDefault();

    if (isConvertingProof) {
      setFileError('Please wait until the proof image finishes converting.');
      return;
    }

    const payload = {
      billingStatus,
      billingRemarks,
    };

    if (String(proofPreview || '').startsWith('data:')) {
      payload.billingProofImageData = proofPreview;
    }

    if (removeBillingProofImage) {
      payload.removeBillingProofImage = true;
    }

    onSubmit(payload);
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/35 px-4 py-6">
      <form
        className="w-full max-w-[560px] overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_24px_70px_rgba(15,23,42,0.24)]"
        onSubmit={handleSubmit}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--theme-border-soft)] px-5 py-4">
          <div className="min-w-0">
            <p className="m-0 text-[0.74rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
              Customer Payment
            </p>
            <h3 className="m-0 mt-1 text-[1.05rem] font-semibold text-[var(--theme-text)]">
              {formatSalesOrderCode(salesOrder)}
            </h3>
          </div>
          <button
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-muted)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            aria-label="Close billing modal"
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="grid gap-4 px-5 py-5">
          <div className="grid gap-2">
            <span className="text-[0.78rem] font-semibold text-[var(--theme-text-muted)]">
              Status
            </span>
            <div className="grid grid-cols-2 gap-2 rounded-[18px] border border-[var(--theme-border)] bg-[var(--theme-surface-soft)] p-1.5 shadow-inner shadow-[rgba(var(--theme-shadow-rgb),0.08)]">
              {['Unpaid', 'Paid'].map((status) => {
                const isSelected = billingStatus === status;

                return (
                  <button
                    key={status}
                    className={`flex min-h-[48px] items-center justify-center gap-2 rounded-[14px] border px-3 text-[0.9rem] font-bold transition ${
                      isSelected
                        ? 'border-[var(--theme-accent)] bg-[var(--theme-accent)] text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.22)]'
                        : 'border-transparent bg-transparent text-[var(--theme-text-muted)] hover:border-[var(--theme-border)] hover:bg-white'
                    }`}
                    type="button"
                    onClick={() => setBillingStatus(status)}
                  >
                    {isSelected ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> : null}
                    {status}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="grid gap-2">
            <span className="text-[0.78rem] font-semibold text-[var(--theme-text-muted)]">
              Remarks
            </span>
            <textarea
              className="min-h-[92px] resize-y rounded-[16px] border border-[var(--theme-border)] bg-white px-3 py-2.5 text-[0.9rem] leading-6 text-[var(--theme-text)] outline-none transition placeholder:text-[var(--theme-text-muted)] focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[color-mix(in_srgb,var(--theme-accent)_18%,transparent)]"
              maxLength={1000}
              value={billingRemarks}
              placeholder="Optional payment or billing notes"
              onChange={(event) => setBillingRemarks(event.target.value)}
            />
          </label>

          <label className="grid cursor-pointer gap-3 rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] p-4 transition hover:bg-[var(--theme-surface-soft)]">
            <input
              className="sr-only"
              type="file"
              accept="image/*"
              onChange={handleProofChange}
            />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-[var(--theme-accent)]">
                  <UploadCloud className="h-5 w-5" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="m-0 text-[0.9rem] font-semibold text-[var(--theme-text)]">
                    Proof of billing
                  </p>
                  <p className="m-0 mt-1 text-[0.76rem] text-[var(--theme-text-muted)]">
                    Optional image up to 50MB. Saved as WEBP.
                  </p>
                </div>
              </div>
              <span className="w-max rounded-full border border-[var(--theme-border)] bg-white px-3 py-1.5 text-[0.78rem] font-semibold text-[var(--theme-text-muted)]">
                {isConvertingProof ? 'Converting...' : 'Browse'}
              </span>
            </div>
          </label>

          {fileError ? (
            <p className="m-0 rounded-[14px] border border-[#f3c6ba] bg-[#fff2ee] px-3 py-2 text-[0.82rem] text-[#a94732]">
              {fileError}
            </p>
          ) : null}

          {hasPreview ? (
            <div className="flex items-center justify-between gap-3 rounded-[16px] border border-[var(--theme-border-soft)] bg-white p-3">
              <div className="relative h-16 w-28 overflow-hidden rounded-lg border border-[var(--theme-border)] bg-[var(--theme-surface-soft)]">
                <img
                  className="h-full w-full object-cover"
                  src={proofPreview}
                  alt="Billing proof preview"
                />
                <button
                  className="absolute inset-0 grid place-items-center bg-black/20 text-white opacity-100 transition hover:bg-black/35"
                  type="button"
                  onClick={() => setIsProofViewerOpen(true)}
                  aria-label="View billing proof fullscreen"
                  title="View fullscreen"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-black/45 shadow-[0_8px_18px_rgba(0,0,0,0.25)]">
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  </span>
                </button>
              </div>
              <button
                className="rounded-full border border-[#f0c8bf] bg-white px-3 py-2 text-[0.78rem] font-semibold text-[#a94732] transition hover:bg-[#fff2ee]"
                type="button"
                onClick={clearProofImage}
              >
                Remove
              </button>
            </div>
          ) : salesOrder.isBillingProofExpired ? (
            <div className="rounded-[14px] border border-[#fecdd3] bg-[#fff1f2] px-3 py-2 text-[0.82rem] font-semibold text-[#e11d48]">
              Previous billing proof has expired.
            </div>
          ) : null}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-[var(--theme-border-soft)] px-5 py-4 sm:flex-row sm:justify-end">
          <button
            className="rounded-full border border-[var(--theme-border)] bg-white px-4 py-2 text-[0.88rem] font-semibold text-[var(--theme-text-muted)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onClose}
            disabled={isSaving || isConvertingProof}
          >
            Cancel
          </button>
          <button
            className="rounded-full bg-[var(--theme-accent)] px-4 py-2 text-[0.88rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.22)] transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
            type="submit"
            disabled={isSaving || isConvertingProof}
          >
            {isConvertingProof ? 'Converting...' : isSaving ? 'Saving...' : 'Save Payment'}
          </button>
        </div>
      </form>

      {isProofViewerOpen ? (
        <div
          className="fixed inset-0 z-[60] grid place-items-center bg-black/80 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-label="Billing proof fullscreen preview"
          onClick={() => setIsProofViewerOpen(false)}
        >
          <div className="relative max-h-full w-full max-w-[min(960px,94vw)]" onClick={(event) => event.stopPropagation()}>
            <button
              className="absolute right-3 top-3 z-10 grid h-10 w-10 place-items-center rounded-full border border-white/30 bg-black/50 text-white transition hover:bg-black/70"
              type="button"
              aria-label="Close billing proof preview"
              onClick={() => setIsProofViewerOpen(false)}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
            <img
              className="max-h-[88vh] w-full rounded-[18px] object-contain shadow-[0_24px_70px_rgba(0,0,0,0.35)]"
              src={proofPreview}
              alt="Billing proof fullscreen preview"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function getSalesOrderEditBaseline(salesOrder) {
  return {
    customerId: String(salesOrder.customerId),
    branch: salesOrder.branch || '',
    seaweedType: salesOrder.seaweedType || '',
    quantity: String(salesOrder.quantity ?? ''),
    dealPrice: String(salesOrder.dealPrice ?? ''),
    soDate: salesOrder.soDate,
  };
}

function formatSalesOrderCode(salesOrder) {
  return salesOrder.soCode || `B-${String(salesOrder.id).padStart(3, '0')}`;
}

export default SalesOrderPage;
